import React from 'react';
import {
    Eye, Users, Image, MessageSquare, Heart, DollarSign, MousePointerClick,
    CalendarCheck, UserCheck, Send, MailOpen, FileText, TrendingUp, TrendingDown, Minus,
} from 'lucide-react';

const ICONS = {
    views: Eye,
    unique_buyers: Users,
    gallery_views: Image,
    enquiries: MessageSquare,
    saves: Heart,
    offers: DollarSign,
    intent_clicks: MousePointerClick,
    ofi_registrations: CalendarCheck,
    ofi_attendees: UserCheck,
    documents: FileText,
    matches_sent: Send,
    matches_opened: MailOpen,
};

function Movement({ label, value }) {
    if (value === null || value === undefined) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400">
                <Minus size={10} /> {label}
            </span>
        );
    }
    const positive = value > 0;
    const flat = value === 0;
    const Icon = flat ? Minus : positive ? TrendingUp : TrendingDown;
    const tone = flat ? 'text-gray-400' : positive ? 'text-emerald-600' : 'text-red-500';
    return (
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${tone}`}>
            <Icon size={10} />
            {flat ? '0%' : `${Math.abs(value).toFixed(0)}%`}
            <span className="font-normal text-gray-400">{label}</span>
        </span>
    );
}

/**
 * The headline metric tiles, each showing movement against the previous 7 days,
 * the previous 30 days and the campaign average.
 */
export default function CampaignSummaryGrid({ metrics = [], onMetricClick }) {
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {metrics.map((metric) => {
                const Icon = ICONS[metric.key] || Eye;
                const clickable = typeof onMetricClick === 'function' && metric.value > 0;
                return (
                    <button
                        key={metric.key}
                        type="button"
                        onClick={() => clickable && onMetricClick(metric)}
                        disabled={!clickable}
                        className={`rounded-xl border border-gray-200 bg-white p-3 text-left transition ${
                            clickable ? 'hover:border-[#55C5D0] hover:shadow-sm' : 'cursor-default'
                        }`}
                    >
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            <Icon size={13} className="text-[#55C5D0]" />
                            <span className="truncate">{metric.label}</span>
                        </div>
                        <div className="mt-1 text-2xl font-extrabold text-[#374151]">
                            {metric.value?.toLocaleString?.() ?? metric.value}
                        </div>
                        <div className="mt-1 flex flex-col gap-0.5">
                            <Movement label="vs last 7d" value={metric.vs_prev_7} />
                            <Movement label="vs campaign avg" value={metric.vs_campaign_avg} />
                        </div>
                        {metric.campaign_total !== metric.value && (
                            <div className="mt-1 text-[10px] font-medium text-gray-400">
                                {metric.campaign_total?.toLocaleString?.() ?? metric.campaign_total} all campaign
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
